<!-- @format -->
<template>
    <div class="campaign-content-widget">
        <div class="widget-header">
            <h3>{{ steps[step].title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="step === 'create'" class="create-step">
                <app-text-editor
                    v-if="clientId"
                    ref="textEditor"
                    v-model="body"
                    :isLoadingContent="isProcessingContent"
                    :config="froalaConfig"
                    :userId="null"
                    :clientId="clientId"
                    :addOns="['teamSignatures', 'library']"
                    :showBrandingColors="false"
                    :injectOptions="['Link', 'Inline']"
                    @insertSignature="insertSignature($event)"
                />
            </div>
            <div v-else-if="step === 'saving'" class="saving-step">
                <loading-screen-svg />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container">
                <app-button class="secondary-btn" @click="openSendTestWidget">
                    Send test email
                </app-button>
                <app-button :loading="steps[step].loading" @click="steps[step].action">
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppTextEditor = () => import('@/components/common/app-text-editor');

import EventBus from '@/scripts/event-bus.js';
import { LogLevel } from "@/scripts/logger";

export default {
    name: 'campaign-content-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-text-editor': AppTextEditor,
        'loading-screen-svg': LoadingScreenSvg,
    },

    props: {
        widget: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            isProcessingContent: false,
            linkAttachments: [],
            campaignId: this.widget.campaignId,
            clientId: null,
            version: this.widget.version || 'A',
            body: '',
            step: 'create',
            steps: {
                create: {
                    title: 'Campaign Email Content',
                    buttonText: 'Save',
                    action: this.saveCampaign,
                    loading: false,
                },

                saving: {
                    title: 'Saving',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },

            froalaConfig: {
                key: this.$helpers.getFroalaKey(),
                toolbarButtons: [
                    'fontFamily',
                    'insert',
                    'fontSize',
                    'align',
                    'formatUL',
                    'insertImage',
                    'emoticons',
                    'bold',
                    'italic',
                    'colorPicker',
                    'insertLink',
                    'highlight',
                ],

                useClasses: false,
                fontFamilySelection: true,
                fontSizeSelection: true,
                paragraphFormatSelection: true,
                fontFamily: this.$helpers.minimalFontStyles,
                emoticonsUseImage: false,

                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],

                requestHeaders: {
                    Authorization: `Bearer ${localStorage.jwt}`,
                },
            },
        };
    },

    created() {
        this.getCampaign();
        EventBus.$on('library-payload-emitted', this.addAttachmentsFromLibrary);
    },

    beforeDestroy() {
        EventBus.$off('library-payload-emitted');
    },

    methods: {
        openSendTestWidget() {
            this.saveBody().then(() => {
                EventBus.$emit('open-widget', {
                    name: 'email-test-widget',
                    campaignId: this.campaignId,
                    version: this.version,
                });
            });
        },

        saveCampaign() {
            this.saveBody().then(this.closeWidget);
        },

        addAttachmentsFromLibrary(payload) {
            var formattedAttachments = this.formatAttachmentsFromLibrary(payload.selectedItems);

            this.processWebPageLinks(formattedAttachments);

            var nonWebPageAttachments = this.getNonWebPageAttachments(formattedAttachments);

            if (payload.attachmentType.toUpperCase() == 'LINK') {
                this.addLinkAttachments(nonWebPageAttachments);
            } else if (payload.attachmentType.toUpperCase() == 'INLINE') {
                var inlineAttachments = this.getInlineAttachments(formattedAttachments);
                var linkAttachments = this.getNonInlineAttachments(formattedAttachments);

                this.addInlineAttachments(inlineAttachments);
                this.addLinkAttachments(linkAttachments);
            }
        },

        formatAttachmentsFromLibrary(rawAttachments) {
            var formattedAttachments = rawAttachments.map((attachment) => {
                return {
                    id: attachment.id,
                    guid: attachment.guid,
                    name: attachment.name,
                    fileType: attachment.fileType,
                    url: attachment.url,
                    description: attachment.description,
                    domain: attachment.domain,
                };
            });

            return formattedAttachments;
        },

        processWebPageLinks(attachments) {
            var webPageAttachments = this.getWebPageAttachments(attachments);
            if (webPageAttachments.length > 0) {
                this.addAsWebPageAttachments(webPageAttachments);
            }
        },

        getWebPageAttachments(attachments) {
            var webPageAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() == 'LINK';
            });

            return webPageAttachments;
        },

        addAsWebPageAttachments(attachments) {
            var request = {
                Links: [],
            };

            request.Links = attachments.map((attachment) => {
                return {
                    Url: attachment.url,
                };
            });

            this.isProcessingContent = true;

            this.$api
                .post('/api/Collaterals/Links/Enrich', request)
                .then((response) => {
                    var enrichedLinks = this.mapEnrichedLinks(attachments, response.data.Links);
                    this.addEnrichedLinks(enrichedLinks);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Unable to attach', { error });
                })
                .finally(() => {
                    this.isProcessingContent = false;
                });
        },

        mapEnrichedLinks(attachments, links) {
            var enrichedLinks = [];

            for (var i = 0; i < links.length; i++) {
                var link = links[i];
                for (var j = 0; j < attachments.length; j++) {
                    var attachment = attachments[j];
                    if (attachment.url == link.Url) {
                        var enrichedLink = {
                            name: attachment.name ? attachment.name : link.Name,
                            title: attachment.title ? attachment.title : link.Title,
                            fileType: link.FileType,
                            domain: link.Domain,
                            url: link.Url,
                            description: null,
                            uploadedByUserId: this.userId,
                            clientId: this.clientId,
                            agencyId: this.agencyId,
                        };

                        enrichedLink.description = this.limitDescription(link.Description || '');

                        enrichedLinks.push(enrichedLink);
                        break;
                    }
                }
            }

            return enrichedLinks;
        },

        limitDescription(description) {
            var limitedDescription = description.replace(/(<([^>]+)>)/gi, '');

            if (limitedDescription.length > 130) {
                var characterDelta = limitedDescription.length - 127;
                limitedDescription = limitedDescription.slice(
                    0,
                    limitedDescription.length - characterDelta
                );
                limitedDescription += '...';
            }

            return limitedDescription;
        },

        addEnrichedLinks(links) {
            var insert = links
                .map((link) => {
                    var attachment = link;
                    return `<a href="${attachment.url}" style="cursor:pointer;text-decoration:none;display:inline-block;padding-top:0px;padding-bottom:0px;padding-right:0px;padding-left:0px;vertical-align:middle;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0px;color:#18AEFF;font-size:14px;" target="_blank">${attachment.title}</a><br/>`;
                })
                .join('');

            this.body += insert;
        },

        getNonWebPageAttachments(attachments) {
            var nonWebPageAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() != 'LINK';
            });

            return nonWebPageAttachments;
        },

        addLinkAttachments(attachments) {
            this.linkAttachments = this.linkAttachments.concat(attachments);
            var insert = this.linkAttachments
                .map((attachment) => {
                    return `<a href="${attachment.url}" data-collateral-guid="${attachment.guid}" style="cursor:pointer;text-decoration:none;display:inline-block;padding-top:0px;padding-bottom:0px;padding-right:0px;padding-left:0px;vertical-align:middle;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0px;color:#18AEFF;font-size:14px;" target="_blank">
                        ${attachment.name}
                    </a><br/>`;
                })
                .join('');

            this.body += insert;

            this.linkAttachments = [];
        },

        getInlineAttachments(attachments) {
            var inlineAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() == 'IMAGE';
            });

            return inlineAttachments;
        },

        getNonInlineAttachments(attachments) {
            var nonInlineAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() != 'IMAGE';
            });

            return nonInlineAttachments;
        },

        addInlineAttachments(inlineAttachments) {
            for (var i = 0; i < inlineAttachments.length; i++) {
                this.body += `<img data-collateral-guid="${inlineAttachments[i].guid}" src="${inlineAttachments[i].url}"/>`;
            }
        },

        getCampaign() {
            this.$api
                .get(
                    `/service/Campaigns/?$filter=Id eq ${this.campaignId}&$select=ConfigurationA,ConfigurationB,ClientId`
                )
                .then((response) => {
                    this.clientId = response.data.value[0].ClientId;

                    if (this.version === 'B') {
                        this.body = response.data.value[0].ConfigurationB
                            ? JSON.parse(response.data.value[0].ConfigurationB).body
                            : '';
                    } else {
                        this.body = response.data.value[0].ConfigurationA
                            ? JSON.parse(response.data.value[0].ConfigurationA).body
                            : '';
                    }
                });
        },

        saveBody() {
            return new Promise((resolve, reject) => {
                if (this.body) {
                    this.step = 'saving';

                    var changes = [
                        {
                            name: this.version === 'B' ? 'ConfigurationB' : 'ConfigurationA',
                            value: JSON.stringify({ body: this.body }),
                        },
                        {
                            name: 'HasBranding',
                            value: null,
                        },
                    ];

                    this.$api
                        .patch(`/api/Campaigns/${this.campaignId}`, changes)
                        .then(() => {
                            this.$helpers.showSuccessNotification('Content successfully saved.');
                            EventBus.$emit(
                                'campaign-configuration:update',
                                { body: this.body },
                                this.version
                            );
                            resolve();
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification('Failed to save content.', { error });
                            reject();
                        })
                        .finally(() => {
                            this.step = 'create';
                        });
                } else {
                    this.$helpers.showFailNotification('Add some content before saving.', { level: LogLevel.INFO });
                    reject();
                }
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        insertSignature(signature) {
            this.body += signature;
        },
    },
};
</script>

<style scoped>
.campaign-content-widget .saving-step {
    text-align: center;
}

.campaign-content-widget .content-wrapper {
    padding: 0;
}

.campaign-content-widget .action-container {
    display: flex;
}

.campaign-content-widget .action-container .btn {
    margin-left: 15px;
}

.campaign-content-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.campaign-content-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.campaign-content-widget .action-container .secondary-btn {
    background: transparent;
    color: #877d88;
    border: 2px solid #877d88;
}

.campaign-content-widget .action-container .secondary-btn:hover {
    background: #e1e0e5;
}

.campaign-content-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}
</style>
